import { FormEvent, useState } from "react"
import { Head, usePage } from "@inertiajs/react"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { CheckoutShell } from "@/components/CheckoutShell"
import { StripeProvider } from "@/components/StripeProvider"
import { Button } from "@/components/ui/button"
import { fetchJson, formatCents } from "@/lib/fetch-json"
import type { PageProps } from "@/types/inertia"

type RecoveryProps = PageProps<{
  token: string
  account_email?: string
  state: "invalid" | "nothing_due" | "due" | "recovered"
  amount_due_cents?: number | null
  past_due?: boolean
  stripe_publishable_key: string
}>

export default function RecoveryShow() {
  const { props } = usePage<RecoveryProps>()

  return (
    <>
      <Head title="Update your payment">
        <meta name="description" content="Update your Flywrite payment method and retry your payment." />
        <meta property="og:title" content="Update your payment" />
        <meta property="og:description" content="Update your Flywrite payment method and retry your payment." />
      </Head>
      <CheckoutShell maxWidth="max-w-md">
        <div className="callout">
          {props.state === "invalid" && (
            <>
              <h1>This link has expired</h1>
              <p className="mt-3 text-sm text-ink-body">
                Payment links expire after 14 days. Log in at{" "}
                <a href="/login">account.flywrite.app</a> to update your card from the billing
                page, or check your email for a newer link.
              </p>
            </>
          )}

          {props.state === "recovered" && (
            <>
              <h1>Payment received 🎉</h1>
              <p className="mt-3 text-sm text-ink-body">
                Thanks! Your subscription is active again and all licenses are restored. A
                confirmation email is on its way.
              </p>
              <p className="mt-4">
                <a href="/login" className="btn btn-secondary btn-small">
                  Go to your account
                </a>
              </p>
            </>
          )}

          {props.state === "nothing_due" && (
            <>
              <h1>You're all set</h1>
              <p className="mt-3 text-sm text-ink-body">
                There's nothing due on your account right now. If you were fixing a failed
                payment, it looks like it already went through.
              </p>
            </>
          )}

          {props.state === "due" && (
            <>
              <h1>Update your payment method</h1>
              <p className="mt-2 text-sm text-ink-body">
                {props.account_email && (
                  <>
                    For <strong>{props.account_email}</strong>.{" "}
                  </>
                )}
                Your Flywrite payment of{" "}
                <strong>{formatCents(props.amount_due_cents ?? 0)}</strong> didn't go through.
                Enter a new card and we'll retry immediately — your licenses stay active in the
                meantime.
              </p>
              <RecoveryForm token={props.token} publishableKey={props.stripe_publishable_key} />
            </>
          )}
        </div>
      </CheckoutShell>
    </>
  )
}

function RecoveryForm({ token, publishableKey }: { token: string; publishableKey: string }) {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)

  async function begin() {
    setStarting(true)
    setError(null)
    const result = await fetchJson<{ client_secret?: string; errors?: Record<string, string> }>(
      `/billing/recover/${token}/setup_intent`,
    )
    setStarting(false)
    if (result.ok && result.data.client_secret) {
      setClientSecret(result.data.client_secret)
    } else {
      setError(result.data.errors?.base ?? "Something went wrong. Please try again.")
    }
  }

  if (!clientSecret) {
    return (
      <div className="mt-5">
        {error && <p className="mb-3 text-sm text-danger-display">{error}</p>}
        <Button onClick={() => void begin()} disabled={starting} className="w-full">
          {starting ? "One moment…" : "Update card & retry payment"}
        </Button>
      </div>
    )
  }

  return (
    <StripeProvider publishableKey={publishableKey} clientSecret={clientSecret}>
      <RecoveryPaymentElement token={token} />
    </StripeProvider>
  )
}

function RecoveryPaymentElement({ token }: { token: string }) {
  const stripe = useStripe()
  const elements = useElements()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setSaving(true)
    setError(null)
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/billing/recover/${token}` },
    })
    setError(result.error?.message ?? "Couldn't save the card. Please try again.")
    setSaving(false)
  }

  return (
    <form onSubmit={save} className="mt-5 space-y-4">
      <PaymentElement />
      {error && <p className="text-sm text-danger-display">{error}</p>}
      <Button type="submit" disabled={!stripe || saving} className="w-full">
        {saving ? "Processing…" : "Save card & retry payment"}
      </Button>
      <p className="text-xs text-ink-muted">Secured by Stripe.</p>
    </form>
  )
}
